import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertCircle, ArrowLeft, CheckCircle2, Clock } from 'lucide-react';
import { AppHeader } from '@/components/AppHeader';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import {
  getSend,
  listSends,
  type SendDetail,
  type SendStatus,
  type SendSummary,
} from '@/lib/sends';
import { formatRelativeTime } from '@/lib/drafts';

export function SentPage() {
  const navigate = useNavigate();
  const [sends, setSends] = useState<SendSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selected, setSelected] = useState<SendDetail | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setSends(await listSends(100));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load sends');
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (!selectedId) {
      setSelected(null);
      setDetailError(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await getSend(selectedId);
        if (!cancelled) setSelected(data);
      } catch (e) {
        if (!cancelled) setDetailError(e instanceof Error ? e.message : 'Failed to load send');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  return (
    <div className="bg-background text-foreground min-h-screen">
      <AppHeader currentTab="sent" />

      <main className="mx-auto max-w-[1200px] px-6 py-12 lg:px-10">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight">Sent</h1>
            <p className="text-muted-foreground mt-1 text-sm">
              {sends === null
                ? 'Loading…'
                : sends.length === 0
                  ? 'No sends yet.'
                  : `${sends.length} send${sends.length === 1 ? '' : 's'} this account`}
            </p>
          </div>
          <Button variant="outline" onClick={() => navigate('/app')}>
            <ArrowLeft className="size-4" /> Drafts
          </Button>
        </div>

        {error ? (
          <Alert variant="destructive" className="mt-6">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}

        {sends === null ? (
          <SendsLoading />
        ) : sends.length === 0 ? (
          <SendsEmpty />
        ) : (
          <Card className="mt-8 gap-0 overflow-hidden p-0">
            <ul className="divide-border divide-y">
              {sends.map((s) => (
                <SendRow
                  key={s.id}
                  send={s}
                  onClick={() => setSelectedId(s.id)}
                  selected={selectedId === s.id}
                />
              ))}
            </ul>
          </Card>
        )}
      </main>

      <Sheet open={!!selectedId} onOpenChange={(next) => !next && setSelectedId(null)}>
        <SheetContent side="right" className="w-full max-w-2xl gap-0 sm:max-w-2xl">
          <SheetHeader>
            <SheetTitle>Send detail</SheetTitle>
          </SheetHeader>
          <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-6">
            {selectedId && !selected && !detailError ? (
              <div className="space-y-3">
                <Skeleton className="h-4 w-1/2" />
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-[60vh] w-full" />
              </div>
            ) : detailError ? (
              <Alert variant="destructive">
                <AlertDescription>{detailError}</AlertDescription>
              </Alert>
            ) : selected ? (
              <div className="space-y-5">
                <DetailRow label="Subject">{selected.subject || '(no subject)'}</DetailRow>
                <DetailRow label="Status">
                  <span className="inline-flex items-center gap-2">
                    <StatusBadge status={selected.status} />
                    {selected.error ? (
                      <span className="text-destructive text-xs">{selected.error}</span>
                    ) : null}
                  </span>
                </DetailRow>
                <DetailRow label="From">
                  {selected.from_name
                    ? `${selected.from_name} <${selected.from_email}>`
                    : selected.from_email}
                </DetailRow>
                {selected.reply_to ? (
                  <DetailRow label="Reply-to">{selected.reply_to}</DetailRow>
                ) : null}
                <DetailRow label="To">
                  <ul className="space-y-0.5 font-mono text-xs">
                    {selected.to.map((addr) => (
                      <li key={addr}>{addr}</li>
                    ))}
                  </ul>
                </DetailRow>
                <DetailRow label="Sent at">
                  {new Date(selected.sent_at).toLocaleString()}
                </DetailRow>
                {selected.draft_id ? (
                  <DetailRow label="Draft">
                    <Link to={`/app/d/${selected.draft_id}`} className="text-primary hover:underline">
                      Open draft → v{selected.draft_version ?? '?'}
                    </Link>
                  </DetailRow>
                ) : null}
                <DetailRow label="Email preview">
                  <div className="border-border mt-2 overflow-hidden rounded-md border bg-white">
                    <iframe
                      title="Sent email"
                      sandbox=""
                      srcDoc={selected.html}
                      style={{ width: '100%', height: '60vh', border: 0 }}
                    />
                  </div>
                </DetailRow>
              </div>
            ) : null}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}

function SendRow({
  send,
  onClick,
  selected,
}: {
  send: SendSummary;
  onClick: () => void;
  selected: boolean;
}) {
  const preview = send.to.slice(0, 2).join(', ');
  const more = send.to.length > 2 ? ` +${send.to.length - 2} more` : '';
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className={cn(
          'hover:bg-muted/60 flex w-full items-start justify-between gap-4 px-5 py-4 text-left transition-colors',
          selected && 'bg-muted/60',
        )}
      >
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="truncate text-sm font-medium">{send.subject || '(no subject)'}</p>
            <StatusBadge status={send.status} />
          </div>
          <p className="text-muted-foreground mt-1 truncate text-xs">
            To {preview}
            {more}
          </p>
          <p className="text-muted-foreground/70 mt-1 truncate text-xs">
            From {send.from_name ? `${send.from_name} <${send.from_email}>` : send.from_email}
          </p>
        </div>
        <div className="text-muted-foreground whitespace-nowrap pt-0.5 text-xs">
          {formatRelativeTime(send.sent_at)}
        </div>
      </button>
    </li>
  );
}

function StatusBadge({ status }: { status: SendStatus }) {
  if (status === 'sent') {
    return (
      <Badge
        variant="outline"
        className="border-success/30 bg-success/10 text-success rounded-full text-[10px] font-medium"
      >
        <CheckCircle2 className="size-3" /> Sent
      </Badge>
    );
  }
  if (status === 'failed') {
    return (
      <Badge variant="destructive" className="rounded-full text-[10px] font-medium">
        <AlertCircle className="size-3" /> Failed
      </Badge>
    );
  }
  return (
    <Badge variant="secondary" className="rounded-full text-[10px] font-medium">
      <Clock className="size-3" /> Queued
    </Badge>
  );
}

function SendsLoading() {
  return (
    <Card className="mt-8 gap-0 overflow-hidden p-0">
      <ul className="divide-border divide-y">
        {[0, 1, 2].map((i) => (
          <li key={i} className="space-y-2 px-5 py-4">
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-3 w-1/3" />
          </li>
        ))}
      </ul>
    </Card>
  );
}

function SendsEmpty() {
  return (
    <Card className="border-border bg-card mt-12 border-dashed text-center">
      <div className="p-12">
        <h2 className="text-xl font-semibold tracking-tight">No sends yet</h2>
        <p className="text-muted-foreground mx-auto mt-2 max-w-md text-sm">
          Once you send an email from a draft, it'll show up here with delivery status.
        </p>
        <Button asChild className="mt-6">
          <Link to="/app">Go to drafts →</Link>
        </Button>
      </div>
    </Card>
  );
}

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-muted-foreground mb-1 text-xs uppercase tracking-wider">{label}</div>
      <div className="text-sm">{children}</div>
    </div>
  );
}
